<script setup lang="ts">
import { useI18n } from '#imports'

export interface VideoSoundButtonProps {
    /** Whether the video it controls currently plays without sound. */
    muted: boolean
    /**
     * Lifts the button over a modal dialog's backdrop, so the sound stays reachable while the
     * dialog is open. The parent teleports it to `#teleports` at the same time: a fixed position
     * alone stays inside the hero's stacking context, under the dialog.
     */
    raised?: boolean
}

defineProps<VideoSoundButtonProps>()

const { t } = useI18n()
</script>

<template>
    <!-- data-video-sound-button makes useDeferredVideoAutoplay leave a tap here to the click, which toggles the sound. -->
    <button
        type="button"
        class="video-sound-button"
        :class="{ 'video-sound-button--raised': raised }"
        :aria-label="t(muted ? 'core.components.VideoSoundButton.unmute' : 'core.components.VideoSoundButton.mute')"
        data-test="video-sound-button"
        data-video-sound-button
    >
        <svg
            class="video-sound-button__icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path d="M4 9.5v5a1 1 0 0 0 1 1h2.6l4.8 3.8a.6.6 0 0 0 1-.5V5.2a.6.6 0 0 0-1-.5L7.6 8.5H5a1 1 0 0 0-1 1Z" />
            <path
                v-if="muted"
                class="video-sound-button__stroke"
                d="m16.5 9.5 5 5m0-5-5 5"
            />
            <template v-else>
                <path
                    class="video-sound-button__stroke"
                    d="M16.2 9a4.3 4.3 0 0 1 0 6"
                />
                <path
                    class="video-sound-button__stroke"
                    d="M18.8 6.5a7.8 7.8 0 0 1 0 11"
                />
            </template>
        </svg>
    </button>
</template>

<style lang="scss" scoped>
// Overlays a full-bleed background video in its top-right corner; a hero whose chrome sits there
// moves it down with --video-sound-button-top, set on the button itself where it gets raised, since
// a variable on the hero does not follow it into the dialog layer. Shares the play button's layer.
.video-sound-button {
    position: absolute;
    top: var(--video-sound-button-top, 16px);
    right: 16px;
    z-index: z(video-play-button);
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 2px solid rgba(255, 255, 255, 0.85);
    border-radius: 50%;
    color: #fff;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(6px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background: rgba(0, 0, 0, 0.6);
    }

    &:active {
        transform: scale(0.94);
    }

    &:focus-visible {
        outline: 3px solid #fff;
        outline-offset: 4px;
    }

    // Raised into the dialog layer: fixed to the viewport, which the landing pages snap to the top
    // while their auth sheet is open, so it keeps its spot; the toast layer sits over the backdrop.
    // #teleports switches pointer events off for everything inside it, like the backdrop it turns them back on.
    &--raised {
        position: fixed;
        z-index: z(toast);
        pointer-events: auto;
    }
}

.video-sound-button__icon {
    width: 22px;
    height: 22px;
    fill: currentcolor;
}

.video-sound-button__stroke {
    fill: none;
    stroke: currentcolor;
    stroke-width: 2;
    stroke-linecap: round;
}
</style>
